<template lang="pug">
  .simple-subscription
    v-container.white.px-6(style="max-width:600px")
      v-layout.my-2
        div.mr-2
          v-icon.mt-1(left color="black" :size="24" @click="$router.push('/admin/clinic/settings')") mdi-arrow-left
        div.title.font-weight-bold {{ $t('subscription') }}

      v-layout.my-6(v-if="!isReady")
        v-row.fill-height.ma-0(align='center' justify='center')
          v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)

      .content-layout(v-else)
        //- Active Subscriptions
        div.my-4
          .s1.mb-2 {{ $t('active_subscriptions') }}

          div(v-if="activeSubscriptions.length === 0")
            v-card.pa-6.text-center(outlined flat)
              v-img.mx-auto(:src="require('@/assets/images/icon_empty.png')" style="width:60px; height:60px" contain)
              .p2.basic600--text.mt-2 {{ $t('no_active_subscription') }}

          v-card.mb-3(v-for="sub in activeSubscriptions" :key="sub.name" outlined flat)
            .pa-4
              .p2 {{ sub.name }}
              div.d-flex.mt-1(v-if="sub.isTrial")
                .s2.text-uppercase {{ $t('trial') }}
              div.d-flex.mt-1(v-else)
                .s2.text-uppercase {{ sub.plan }}
              .c1.primary400--text.mt-1(v-if="sub.isTrial && sub.endDate")
                span(v-if="sub.expired") {{ $t('trial_ended_at') }} {{ sub.endDate | humanizeDate }}
                span(v-else) {{ $t('trial_ends') }}: {{ sub.endDate | humanizeDate }}
              .c1.mt-1(v-if="!sub.isTrial && sub.endDate") {{ $t('next_billing_date') }}: {{ sub.endDate | humanizeDate }}
              .c1.primary400--text.mt-1(v-if="!sub.isTrial && sub.contractEndDate") {{ $t('contract_ends') }}: {{ sub.contractEndDate | humanizeDate }}

        v-divider.my-4

        //- Total Due (primary owner only)
        template(v-if="isPrimaryOwner")
          div.my-4
            .s1.mb-2 {{ $t('total_due') }}
            v-card(outlined flat)
              .pa-4
                v-layout.align-center(v-if="loadingBills")
                  v-progress-circular(:width="2" :size="20" color="primary" indeterminate)
                template(v-else)
                  v-layout.align-center(v-if="totalDue > 0")
                    .h5.font-weight-bold {{ totalDue | displayPrice($currency) }}
                  .p2.basic600--text(v-else) {{ $t('no_pending_bills') }}

          v-divider.my-4

        //- Payment Cards (primary owner only)
        template(v-if="isPrimaryOwner")
          div.my-4
            card-list(@change="onCardChange")

          v-divider.my-4

          //- Pay Button
          div.my-4(v-if="totalDue > 0")
            v-btn(block color="info500" dark depressed :loading="isProcessing" :disabled="!selectedCard" @click="payAllBills")
              .button-AA-medium.white--text {{ $t('pay_now') }}
            .c1.basic600--text.text-center.mt-2(v-if="!selectedCard") {{ $t('select_card_to_pay') }}

          v-divider.my-4

        //- Share Payment Link
        div.my-4
          .s1.mb-2 {{ $t('share_payment_link') }}
          v-card.pa-4(outlined flat)
            .p2.basic600--text.mb-3 {{ $t('share_payment_link_desc') }}
            v-text-field.p1(:value="paymentLink" outlined dense solo flat readonly background-color="basic200" height="40" hide-details @click="$event.target.select()")
            v-btn.mt-3(block outlined color="info500" @click="copyPaymentLink")
              v-icon.mr-2(small) mdi-content-copy
              .button-Aa-medium {{ $t('copy_link') }}

    //- Processing dialog
    y-dialog(v-model='isProcessing' persistent width='300')
      v-card(color='primary' dark)
        v-card-text.pt-3
          | {{ $t('processing_payment') }}
          v-progress-linear.mb-0.mt-4(indeterminate color='white')
          div.mt-4.font-weight-bold {{ $t('do_not_refresh') }}

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import CardList from '@/components/billing/CardList.vue'

export default {
  name: 'SimpleSubscription',
  components: {
    CardList
  },
  data: () => ({
    selectedCard: null,
    hasPendingBills: false,
    loadingBills: false,
    totalDue: 0,
    unpaidBills: [],
    isProcessing: false
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    isPrimaryOwner () {
      const member = this.$store.state.user.member
      return member && this.company && member.id === this.company.primary_owner
    },
    ownerEmail () {
      const member = this.$store.state.user.member
      return member && member.user ? member.user.email : ''
    },
    paymentLink () {
      const base = window.location.origin
      const slug = this.company ? this.company.slug : ''
      return `${base}/pay-subscription?slug=${slug}&email=${encodeURIComponent(this.ownerEmail)}`
    },
    ...mapState('subscriptions', [
      'isReady',
      'cmsTrial',
      'cmsSubscription',
      'appointmentTrial',
      'appointmentSubscription',
      'reviewTrial',
      'reviewSubscription',
      'purchaseHubTrial',
      'purchaseHubSubscription',
      'marketingTrial',
      'marketingSubscription',
    ]),
    activeSubscriptions () {
      const subs = []
      const modules = [
        { name: this.$t('clinic'), trial: this.cmsTrial, sub: this.cmsSubscription },
        { name: this.$t('appointment'), trial: this.appointmentTrial, sub: this.appointmentSubscription },
        { name: this.$t('review'), trial: this.reviewTrial, sub: this.reviewSubscription },
        { name: 'Purchase Hub', trial: this.purchaseHubTrial, sub: this.purchaseHubSubscription },
        { name: 'Marketing', trial: this.marketingTrial, sub: this.marketingSubscription },
      ]
      for (const m of modules) {
        if (m.trial && m.trial.value && !m.trial.data.expired) {
          subs.push({
            name: m.name,
            plan: this.$t('trial'),
            isTrial: true,
            expired: false,
            endDate: m.trial.data.end_date,
            contractEndDate: null
          })
        } else if (m.sub && m.sub.value && !m.sub.data.expired) {
          const code = m.sub.data.contract.code
          const isYearly = code.includes('_YY')
          subs.push({
            name: m.name,
            plan: this.getContractDisplayName(code),
            isTrial: false,
            expired: false,
            endDate: m.sub.data.end_date,
            contractEndDate: (isYearly || code.includes('_YM')) ? m.sub.data.contract_end_date : null
          })
        }
      }
      return subs
    },
  },
  mounted () {
    if (this.isPrimaryOwner) {
      this.checkPendingBills()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    getContractDisplayName (code) {
      if (code.includes('_YY')) return this.$t('yearly')
      if (code.includes('_MM')) return this.$t('monthly')
      if (code.includes('_YM')) return this.$t('yearly')
      return ''
    },
    onCardChange (card) {
      this.selectedCard = card
    },
    checkPendingBills () {
      this.loadingBills = true
      this.$axios.get(`/subscription/bills/?company=${this.company.id}&ordering=-created&paid=false&page_size=1000`)
        .then((resp) => {
          this.hasPendingBills = resp.data.count > 0
          this.unpaidBills = resp.data.results || []
          this.totalDue = this.unpaidBills.reduce((sum, bill) => {
            return sum + (parseFloat(bill.total_amount) || 0)
          }, 0)
        })
        .catch(() => {})
        .finally(() => {
          this.loadingBills = false
        })
    },
    goToBills () {
      this.$router.push('/admin/billing/clinic-bills')
    },
    copyPaymentLink () {
      navigator.clipboard.writeText(this.paymentLink).then(() => {
        this.showMessage(['success', this.$t('link_copied')])
      })
    },
    async payAllBills () {
      if (!this.selectedCard || this.unpaidBills.length === 0) return
      this.isProcessing = true
      try {
        for (const bill of this.unpaidBills) {
          if (parseFloat(bill.total_amount) <= 0) continue
          await this.$axios.post(`/subscription/bills/${bill.id}/charge_card/`, {
            card: this.selectedCard.id
          })
        }
        this.showMessage(['success', this.$t('payment_successful')])
        this.checkPendingBills()
      } catch {
        this.showMessage(['error', this.$t('payment_failed')])
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>

<i18n>
{
  "en": {
    "subscription": "Subscription",
    "active_subscriptions": "Active Subscriptions",
    "no_active_subscription": "No active subscriptions",
    "trial": "Trial",
    "active": "Active",
    "trial_ends": "Trial ends",
    "trial_ended_at": "Trial ended at",
    "next_billing_date": "Next billing date",
    "contract_ends": "Contract ends",
    "total_due": "Total Due",
    "pay_now": "PAY NOW",
    "select_card_to_pay": "Please add or select a card above to pay",
    "processing_payment": "Processing payment...",
    "do_not_refresh": "Please do not refresh or navigate away from this screen",
    "payment_successful": "Payment successful",
    "no_pending_bills": "No pending bills",
    "share_payment_link": "Share Payment Link",
    "share_payment_link_desc": "Share this link with the account owner to make a subscription payment",
    "copy_link": "Copy Link",
    "link_copied": "Payment link copied to clipboard",
    "payment_failed": "Payment failed. Please try again.",
    "clinic": "Clinic",
    "appointment": "Appointment",
    "review": "Review",
    "monthly": "Monthly",
    "yearly": "Yearly"
  },
  "id": {
    "subscription": "Langganan",
    "active_subscriptions": "Langganan Aktif",
    "no_active_subscription": "Tidak ada langganan aktif",
    "trial": "Uji Coba",
    "active": "Aktif",
    "trial_ends": "Uji coba berakhir",
    "trial_ended_at": "Uji coba berakhir pada",
    "next_billing_date": "Tanggal tagihan berikutnya",
    "contract_ends": "Kontrak berakhir",
    "total_due": "Total Tertunggak",
    "pay_now": "BAYAR SEKARANG",
    "select_card_to_pay": "Silakan tambah atau pilih kartu di atas untuk membayar",
    "processing_payment": "Memproses pembayaran...",
    "do_not_refresh": "Jangan muat ulang atau keluar dari halaman ini",
    "payment_successful": "Pembayaran berhasil",
    "no_pending_bills": "Tidak ada tagihan tertunggak",
    "share_payment_link": "Bagikan Link Pembayaran",
    "share_payment_link_desc": "Bagikan link ini kepada pemilik akun untuk melakukan pembayaran langganan",
    "copy_link": "Salin Link",
    "link_copied": "Link pembayaran disalin ke clipboard",
    "payment_failed": "Pembayaran gagal. Silakan coba lagi.",
    "clinic": "Klinik",
    "appointment": "Janji Temu",
    "review": "Ulasan",
    "monthly": "Bulanan",
    "yearly": "Tahunan"
  }
}
</i18n>
